/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        ZonesAgentPage.tsx
 * @description Page mobile complete pour les agents terrain
 *              Gere le cycle complet: demarrage -> zones -> terminaison
 *
 * @features    - Affichage infos intervention
 *              - Bouton demarrage intervention (horodatage auto)
 *              - Checklist des zones avec cochage
 *              - Bouton terminaison avec recap
 *              - Signalement d'incidents
 *
 * @version     2.2
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deneignementService } from '@/services/deneignementService';
import { getBackendUrl } from '@/services/api';
import { zoneService } from '@/services/zoneService';
import { ZoneChecklist } from '@/components/zones/ZoneChecklist';
import { IncidentReportModal, InterventionRecap, OfflineIndicator } from '@/components/mobile';
import {
  ArrowLeftIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  BuildingOfficeIcon,
  PlayIcon,
  StopIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  UserGroupIcon,
  TruckIcon,
  CameraIcon,
  PhotoIcon,
  XMarkIcon,
  TrashIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import {
  PlayIcon as PlayIconSolid,
  StopIcon as StopIconSolid,
} from '@heroicons/react/24/solid';
import { PRIORITE_LABELS, STATUT_LABELS, ZONE_LABELS } from '@/types';
import { clsx } from 'clsx';

/**
 * Page mobile pour les agents terrain - Gestion complete de l'intervention
 * Jalonnement terrain v2.2: Timeline demarrage -> zones -> terminaison
 */
export function ZonesAgentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deneignementId = Number(id);

  // Etats locaux
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [showEarlyTerminationModal, setShowEarlyTerminationModal] = useState(false);
  const [earlyTerminationReason, setEarlyTerminationReason] = useState('');
  const [earlyTerminationComment, setEarlyTerminationComment] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState<'avant' | 'apres' | null>(null);
  const [editingImmatriculation, setEditingImmatriculation] = useState(false);
  const [immatriculationInput, setImmatriculationInput] = useState('');

  // Chargement de l'intervention
  const {
    data: intervention,
    isLoading: isLoadingIntervention,
    error: errorIntervention,
  } = useQuery({
    queryKey: ['deneignement', deneignementId],
    queryFn: () => deneignementService.get(deneignementId),
    enabled: !!deneignementId,
  });

  // Chargement des zones
  const {
    data: zonesData,
    isLoading: isLoadingZones,
    error: errorZones,
    refetch: refetchZones,
  } = useQuery({
    queryKey: ['deneignement-zones', deneignementId],
    queryFn: () => zoneService.listDeneignementZones(deneignementId),
    enabled: !!deneignementId,
    refetchInterval: 30000, // Rafraichir toutes les 30 secondes
  });

  // Mutation pour demarrer l'intervention
  const demarrerMutation = useMutation({
    mutationFn: () => deneignementService.demarrer(deneignementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Erreur lors du demarrage';
      alert(message);
    },
  });

  // Mutation pour renseigner/modifier/effacer l'immatriculation avion
  // NB: appel direct a l'API (pas de file offline) : contrairement a ce que suggerait la
  // planification initiale, demarrer/terminer/zones sur cette page n'utilisent eux-memes
  // aucune file offline malgre l'existence de syncService.queueInterventionStart/End et
  // queueZoneStart/End (jamais appeles nulle part dans le code) - le mode "hors ligne" de
  // syncService.ts n'est donc pas reellement branche sur le parcours agent actuel. Cabler
  // cette action sur une file que rien d'autre n'utilise aurait ete incoherent ; le sujet
  // (retro-brancher tout le parcours agent sur syncService) merite un chantier a part.
  const immatriculationMutation = useMutation({
    mutationFn: (valeur: string | null) =>
      deneignementService.updateImmatriculation(deneignementId, valeur),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
      setEditingImmatriculation(false);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error
        || error?.response?.data?.errors
        || error?.message
        || 'Erreur lors de la mise a jour de l\'immatriculation';
      alert(message);
    },
  });

  // Mutation pour terminer l'intervention
  const terminerMutation = useMutation({
    mutationFn: (options?: {
      travauxEffectues?: boolean;
      motifNonRealisation?: string;
      commentaireNonRealisation?: string;
    }) => deneignementService.terminer(deneignementId, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
      setShowRecap(true);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Erreur lors de la cloture';
      alert(message);
    },
  });

  // Mutation pour uploader une photo
  const uploadPhotoMutation = useMutation({
    mutationFn: ({ type, file }: { type: 'avant' | 'apres'; file: File }) =>
      deneignementService.uploadPhoto(deneignementId, type, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
      setUploadingPhoto(null);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Erreur lors de l\'upload';
      alert(message);
      setUploadingPhoto(null);
    },
  });

  // Mutation pour supprimer une photo
  const deletePhotoMutation = useMutation({
    mutationFn: (type: 'avant' | 'apres') =>
      deneignementService.deletePhoto(deneignementId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Erreur lors de la suppression';
      alert(message);
    },
  });

  // Handler pour la selection de photo
  const handlePhotoSelect = (type: 'avant' | 'apres', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadingPhoto(type);
      uploadPhotoMutation.mutate({ type, file });
    }
  };

  const isLoading = isLoadingIntervention || isLoadingZones;
  const error = errorIntervention || errorZones;
  const isPending = demarrerMutation.isPending || terminerMutation.isPending;

  // Determiner l'etape actuelle
  const canStart = intervention && ['affectee', 'en_deplacement'].includes(intervention.statut);
  const isInProgress = intervention?.statut === 'en_cours';
  const isCompleted = intervention && ['cloturee', 'validee'].includes(intervention.statut);
  const allZonesCompleted = zonesData?.zones.every((z) => z.statut === 'termine') ?? false;

  // Verifier si une zone est en cours
  const zones = zonesData?.zones || [];
  const hasZoneInProgress = zones.some((z) => z.statut === 'en_cours');
  // Verifier si au moins une zone a ete traitee (terminee)
  const hasAnyZoneCompleted = zones.some((z) => z.statut === 'termine');
  // Verifier si aucun jalonnement n'a ete realise (toutes les zones sont a_faire ou pas de zones)
  const noZoneStarted = zones.length === 0 || zones.every((z) => z.statut === 'a_faire');
  // Necessiter une validation si aucune zone traitee
  const requiresEarlyTerminationReason = !hasAnyZoneCompleted;

  // On peut terminer si en cours ET aucune zone n'est en cours
  const canFinish = isInProgress && !hasZoneInProgress;

  // Calculer le temps ecoule
  const getElapsedTime = () => {
    if (!intervention?.heureDebutReelle) return null;
    const start = new Date(intervention.heureDebutReelle);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return hours > 0 ? `${hours}h ${mins}min` : `${mins} min`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !intervention) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">Intervention non trouvee</p>
          <button
            onClick={() => navigate('/deneignements')}
            className="btn-primary"
          >
            Retour a la liste
          </button>
        </div>
      </div>
    );
  }

  const prioriteColor = {
    critique: 'bg-red-500',
    urgente: 'bg-orange-500',
    normale: 'bg-gray-500',
  }[intervention.priorite];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Header fixe */}
      <header className="sticky top-0 z-10 bg-white shadow-sm mt-0">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/agent')}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 active:bg-gray-200"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 truncate">
                {intervention.numeroOrdre}
              </h1>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <MapPinIcon className="w-4 h-4" />
                <span className="truncate">{intervention.parking?.code || 'N/A'}</span>
              </div>
            </div>
            <span
              className={clsx(
                'px-3 py-1 rounded-full text-sm font-medium text-white',
                prioriteColor
              )}
            >
              {PRIORITE_LABELS[intervention.priorite]}
            </span>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="p-4 pb-32">
        {/* Statut actuel */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">Statut</span>
            <span
              className={clsx(
                'px-3 py-1 rounded-full text-sm font-medium text-white',
                intervention.statut === 'en_cours' && 'bg-red-500',
                intervention.statut === 'affectee' && 'bg-orange-500',
                intervention.statut === 'cloturee' && intervention.travauxEffectues !== false && 'bg-green-500',
                intervention.statut === 'cloturee' && intervention.travauxEffectues === false && 'bg-orange-500',
                intervention.statut === 'validee' && 'bg-green-600',
                !['en_cours', 'affectee', 'cloturee', 'validee'].includes(intervention.statut) && 'bg-gray-500'
              )}
            >
              {intervention.statut === 'cloturee' && intervention.travauxEffectues === false
                ? 'Cloturee - Non effectuee'
                : STATUT_LABELS[intervention.statut]}
            </span>
          </div>

          {/* Affichage du detail si cloturee sans travaux */}
          {isCompleted && intervention.travauxEffectues === false && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-800">
                    Intervention non realisee
                  </p>
                  {intervention.motifNonRealisation && (
                    <p className="text-sm text-orange-700 mt-1">
                      <strong>Motif:</strong> {
                        {
                          conditions_meteo: 'Conditions meteo defavorables',
                          panne_materiel: 'Panne de materiel',
                          urgence_autre_intervention: 'Urgence - autre intervention prioritaire',
                          parking_occupe: 'Parking occupe / inaccessible',
                          annulation_compagnie: 'Annulation par la compagnie',
                          manque_effectif: 'Manque d\'effectif',
                          autre: 'Autre motif',
                        }[intervention.motifNonRealisation] || intervention.motifNonRealisation
                      }
                    </p>
                  )}
                  {intervention.commentaireNonRealisation && (
                    <p className="text-sm text-orange-600 mt-1">
                      <strong>Detail:</strong> {intervention.commentaireNonRealisation}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Affichage si cloturee avec travaux */}
          {isCompleted && intervention.travauxEffectues !== false && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                <p className="text-sm font-medium text-green-800">
                  Travaux realises avec succes
                </p>
              </div>
            </div>
          )}

          {isInProgress && intervention.heureDebutReelle && (
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg p-2">
              <ClockIcon className="w-5 h-5 text-blue-500" />
              <span>En cours depuis <strong>{getElapsedTime()}</strong></span>
            </div>
          )}
        </div>

        {/* Bouton DEMARRER (si pas encore demarre) */}
        {canStart && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
            <div className="text-center mb-4">
              <PlayIconSolid className="w-16 h-16 text-green-500 mx-auto mb-2" />
              <h2 className="text-lg font-bold text-gray-900">Pret a demarrer ?</h2>
              <p className="text-sm text-gray-600">
                Appuyez sur le bouton pour commencer l'intervention
              </p>
            </div>
            <button
              onClick={() => demarrerMutation.mutate()}
              disabled={isPending}
              className={clsx(
                'w-full flex items-center justify-center gap-3 py-5 px-6 rounded-xl text-xl font-bold text-white transition-all',
                'bg-green-500 hover:bg-green-600 active:bg-green-700',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'shadow-lg shadow-green-500/30'
              )}
            >
              <PlayIconSolid className="w-8 h-8" />
              <span>DEMARRER L'INTERVENTION</span>
            </button>
            {demarrerMutation.isError && (
              <p className="text-red-500 text-sm text-center mt-2">
                Erreur: {(demarrerMutation.error as Error)?.message || 'Impossible de demarrer'}
              </p>
            )}
          </div>
        )}

        {/* Infos intervention */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Informations
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Date</p>
                <p className="font-medium">
                  {new Date(intervention.dateIntervention).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Heure souhaitee</p>
                <p className="font-medium">
                  {new Date(intervention.heureSouhaitee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
            {intervention.heureDebutReelle && (
              <div className="flex items-center gap-2">
                <PlayIcon className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-xs text-gray-500">Debut reel</p>
                  <p className="font-medium text-green-600">
                    {new Date(intervention.heureDebutReelle).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            )}
            {intervention.heureFinReelle && (
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500">Fin reelle</p>
                  <p className="font-medium text-blue-600">
                    {new Date(intervention.heureFinReelle).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 col-span-2">
              <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Compagnie</p>
                <p className="font-medium">
                  {intervention.compagnie.codeIata} - {intervention.compagnie.nom}
                </p>
              </div>
            </div>
            {intervention.equipes && intervention.equipes.length > 0 && (
              <div className="flex items-center gap-2 col-span-2">
                <UserGroupIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Equipe</p>
                  <p className="font-medium">
                    {intervention.equipes.map((e) => e.nom).join(', ')}
                  </p>
                </div>
              </div>
            )}
            {intervention.zone && (
              <div className="flex items-center gap-2 col-span-2">
                <MapPinIcon className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-xs text-gray-500">Zone demandee</p>
                  <p className="font-medium text-amber-700">
                    {ZONE_LABELS[intervention.zone] || intervention.zone}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Immatriculation avion (non obligatoire) */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            {editingImmatriculation ? (
              <div>
                <label htmlFor="immatriculationAvionAgent" className="text-xs text-gray-500 mb-1 block">
                  Immatriculation avion
                </label>
                <div className="flex gap-2">
                  <input
                    id="immatriculationAvionAgent"
                    type="text"
                    value={immatriculationInput}
                    onChange={(e) => setImmatriculationInput(e.target.value.toUpperCase())}
                    maxLength={12}
                    placeholder="Ex: F-GKXA"
                    className="input flex-1 uppercase"
                    autoFocus
                  />
                  <button
                    onClick={() => immatriculationMutation.mutate(immatriculationInput.trim() || null)}
                    disabled={immatriculationMutation.isPending}
                    className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-50"
                  >
                    Enregistrer
                  </button>
                  <button
                    onClick={() => setEditingImmatriculation(false)}
                    disabled={immatriculationMutation.isPending}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm"
                  >
                    Annuler
                  </button>
                </div>
                {intervention.immatriculationAvion && (
                  <button
                    onClick={() => immatriculationMutation.mutate(null)}
                    disabled={immatriculationMutation.isPending}
                    className="text-xs text-gray-500 mt-2 underline"
                  >
                    Aucun avion present (avion reparti / non concerne)
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <PaperAirplaneIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Immatriculation avion</p>
                    <p className={clsx(
                      'font-medium',
                      intervention.immatriculationAvion ? 'text-gray-900' : 'text-gray-400 italic'
                    )}>
                      {intervention.immatriculationAvion || 'Aucun avion present'}
                    </p>
                  </div>
                </div>
                {!isCompleted && (
                  <button
                    onClick={() => {
                      setImmatriculationInput(intervention.immatriculationAvion || '');
                      setEditingImmatriculation(true);
                    }}
                    className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200"
                    title={intervention.immatriculationAvion ? 'Modifier' : 'Renseigner l\'avion'}
                  >
                    <PencilIcon className="w-5 h-5 text-gray-500" />
                  </button>
                )}
              </div>
            )}
          </div>

          {intervention.commentaire && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-1">Instructions</p>
              <p className="text-sm text-gray-700">{intervention.commentaire}</p>
            </div>
          )}
        </div>

        {/* Section Photos (visible si intervention demarree) */}
        {(isInProgress || isCompleted) && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <CameraIcon className="w-5 h-5" />
              Photos de l'intervention
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Photo AVANT */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photo AVANT
                </label>
                {intervention.photoAvant ? (
                  <div className="relative">
                    <img
                      src={getBackendUrl(intervention.photoAvant)}
                      alt="Photo avant intervention"
                      className="w-full h-32 object-cover rounded-lg border border-gray-200"
                    />
                    {isInProgress && (
                      <div className="flex gap-1 mt-1">
                        <label className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-primary-50 text-primary-700 rounded cursor-pointer hover:bg-primary-100">
                          <ArrowPathIcon className="w-3.5 h-3.5" />
                          Recharger
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => handlePhotoSelect('avant', e)}
                            disabled={uploadingPhoto !== null}
                          />
                        </label>
                        <button
                          onClick={() => deletePhotoMutation.mutate('avant')}
                          disabled={deletePhotoMutation.isPending}
                          className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                          Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <label
                    className={clsx(
                      'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all',
                      isInProgress
                        ? 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                        : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    )}
                  >
                    {uploadingPhoto === 'avant' ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
                    ) : (
                      <>
                        <PhotoIcon className="w-10 h-10 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">
                          {isInProgress ? 'Ajouter photo' : 'Non fournie'}
                        </span>
                      </>
                    )}
                    {isInProgress && (
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handlePhotoSelect('avant', e)}
                        disabled={uploadingPhoto !== null}
                      />
                    )}
                  </label>
                )}
              </div>

              {/* Photo APRES */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photo APRES
                </label>
                {intervention.photoApres ? (
                  <div className="relative">
                    <img
                      src={getBackendUrl(intervention.photoApres)}
                      alt="Photo apres intervention"
                      className="w-full h-32 object-cover rounded-lg border border-gray-200"
                    />
                    {isInProgress && (
                      <div className="flex gap-1 mt-1">
                        <label className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-primary-50 text-primary-700 rounded cursor-pointer hover:bg-primary-100">
                          <ArrowPathIcon className="w-3.5 h-3.5" />
                          Recharger
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => handlePhotoSelect('apres', e)}
                            disabled={uploadingPhoto !== null}
                          />
                        </label>
                        <button
                          onClick={() => deletePhotoMutation.mutate('apres')}
                          disabled={deletePhotoMutation.isPending}
                          className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                          Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <label
                    className={clsx(
                      'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all',
                      isInProgress
                        ? 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                        : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    )}
                  >
                    {uploadingPhoto === 'apres' ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
                    ) : (
                      <>
                        <PhotoIcon className="w-10 h-10 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">
                          {isInProgress ? 'Ajouter photo' : 'Non fournie'}
                        </span>
                      </>
                    )}
                    {isInProgress && (
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handlePhotoSelect('apres', e)}
                        disabled={uploadingPhoto !== null}
                      />
                    )}
                  </label>
                )}
              </div>
            </div>
            {isInProgress && !intervention.photoAvant && !intervention.photoApres && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                Utilisez l'appareil photo pour documenter l'intervention
              </p>
            )}
          </div>
        )}

        {/* Section Zones (visible si intervention demarree) */}
        {(isInProgress || isCompleted) && (
          <>
            {/* Titre section zones */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Zones a traiter</h2>
              <button
                onClick={() => refetchZones()}
                className="text-sm text-primary-600 font-medium hover:underline"
              >
                Actualiser
              </button>
            </div>

            {/* Liste des zones */}
            {zonesData && (
              <ZoneChecklist
                deneignementId={deneignementId}
                zones={zonesData.zones}
                progression={zonesData.progression}
                onZoneUpdate={() => refetchZones()}
                readOnly={isCompleted}
                zoneDemandee={intervention.zone}
              />
            )}
          </>
        )}

        {/* Message si zones non disponibles avant demarrage */}
        {canStart && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
            <ClockIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600">
              Demarrez l'intervention pour acceder aux zones
            </p>
          </div>
        )}
      </main>

      {/* Footer fixe avec actions */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-inset-bottom">
        {/* Bouton Signaler incident */}
        {isInProgress && (
          <div className="px-4 pt-3">
            <button
              onClick={() => setShowIncidentModal(true)}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-orange-300 bg-orange-50 text-orange-700 font-medium"
            >
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span>Signaler un incident</span>
            </button>
          </div>
        )}

        {/* Actions principales */}
        <div className="p-4">
          {/* Progression et bouton terminer */}
          {isInProgress && (
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">
                    Progression
                    {requiresEarlyTerminationReason && (
                      <span className="ml-1 text-orange-500">(aucune zone traitee)</span>
                    )}
                  </span>
                  <span className="text-lg font-bold text-primary-600">
                    {zonesData?.progression.toFixed(0) || 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={clsx(
                      'h-2 rounded-full transition-all duration-500',
                      requiresEarlyTerminationReason
                        ? 'bg-orange-400'
                        : 'bg-gradient-to-r from-blue-500 to-green-500'
                    )}
                    style={{ width: `${zonesData?.progression || 0}%` }}
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  // Si aucun jalonnement n'a ete realise, demander un motif obligatoire
                  if (requiresEarlyTerminationReason) {
                    setShowEarlyTerminationModal(true);
                  } else {
                    const zonesRestantes = zones.filter(z => z.statut !== 'termine').length;
                    const message = zonesRestantes > 0
                      ? `${zonesRestantes} zone(s) non terminee(s). Voulez-vous quand meme terminer l'intervention ?`
                      : 'Voulez-vous terminer cette intervention ?';
                    if (confirm(message)) {
                      terminerMutation.mutate({ travauxEffectues: true });
                    }
                  }
                }}
                disabled={isPending || !canFinish}
                className={clsx(
                  'flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-bold text-white transition-all',
                  canFinish
                    ? 'bg-red-500 hover:bg-red-600 active:bg-red-700 shadow-lg shadow-red-500/30'
                    : 'bg-gray-300 cursor-not-allowed'
                )}
                title={hasZoneInProgress ? 'Terminez d\'abord la zone en cours' : ''}
              >
                <StopIconSolid className="w-6 h-6" />
                <span>Terminer</span>
              </button>
              {hasZoneInProgress && (
                <p className="text-xs text-orange-600 mt-1 text-center">
                  Une zone est en cours
                </p>
              )}
              {!hasZoneInProgress && requiresEarlyTerminationReason && isInProgress && (
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Un motif sera demande
                </p>
              )}
            </div>
          )}

          {/* Intervention terminee */}
          {isCompleted && (
            <div className="flex items-center gap-4">
              <div className="flex-1 flex items-center gap-2 text-green-700">
                <CheckCircleIcon className="w-6 h-6" />
                <span className="font-semibold">Intervention terminee</span>
              </div>
              <button
                onClick={() => setShowRecap(true)}
                className="flex items-center gap-2 py-2 px-4 rounded-lg bg-primary-100 text-primary-700 font-medium"
              >
                <DocumentTextIcon className="w-5 h-5" />
                <span>Recap</span>
              </button>
            </div>
          )}
        </div>
      </footer>

      {/* Modal signalement incident */}
      {showIncidentModal && (
        <IncidentReportModal
          deneignementId={deneignementId}
          onClose={() => setShowIncidentModal(false)}
          onSubmit={() => {
            setShowIncidentModal(false);
            // Rafraichir les donnees
            queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
          }}
        />
      )}

      {/* Modal recapitulatif */}
      {showRecap && intervention && zonesData && (
        <InterventionRecap
          intervention={intervention}
          zones={zonesData.zones}
          onClose={() => setShowRecap(false)}
          onDownloadPdf={() => {
            deneignementService.downloadPdf(deneignementId).then((blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${intervention.numeroOrdre}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
            });
          }}
        />
      )}

      {/* Modal terminaison anticipee (sans jalonnement) */}
      {showEarlyTerminationModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowEarlyTerminationModal(false)}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Terminer sans jalonnement
                  </h3>
                  <p className="text-sm text-gray-500">
                    Aucune zone n'a ete traitee
                  </p>
                </div>
              </div>

              {/* Contenu */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motif de terminaison anticipee *
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={earlyTerminationReason}
                    onChange={(e) => setEarlyTerminationReason(e.target.value)}
                  >
                    <option value="">Selectionner un motif</option>
                    <option value="conditions_meteo">Conditions meteo defavorables</option>
                    <option value="panne_materiel">Panne de materiel</option>
                    <option value="urgence_autre_intervention">Urgence - autre intervention prioritaire</option>
                    <option value="parking_occupe">Parking occupe / inaccessible</option>
                    <option value="annulation_compagnie">Annulation par la compagnie</option>
                    <option value="manque_effectif">Manque d'effectif</option>
                    <option value="autre">Autre motif</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commentaire {earlyTerminationReason === 'autre' ? '*' : '(optionnel)'}
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    rows={3}
                    placeholder="Decrivez la situation..."
                    value={earlyTerminationComment}
                    onChange={(e) => setEarlyTerminationComment(e.target.value)}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowEarlyTerminationModal(false);
                    setEarlyTerminationReason('');
                    setEarlyTerminationComment('');
                  }}
                  className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    if (!earlyTerminationReason) {
                      alert('Veuillez selectionner un motif');
                      return;
                    }
                    if (earlyTerminationReason === 'autre' && !earlyTerminationComment.trim()) {
                      alert('Veuillez preciser le motif dans le commentaire');
                      return;
                    }
                    // Envoyer le motif et commentaire au backend
                    setShowEarlyTerminationModal(false);
                    terminerMutation.mutate({
                      travauxEffectues: false,
                      motifNonRealisation: earlyTerminationReason,
                      commentaireNonRealisation: earlyTerminationComment || undefined,
                    });
                  }}
                  disabled={!earlyTerminationReason || terminerMutation.isPending}
                  className="flex-1 py-2 px-4 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {terminerMutation.isPending ? 'Terminaison...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ZonesAgentPage;
